import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';

const CONSENT_KEY = 'consent.v1';

const ConsentBanner = () => {
  const [open, setOpen] = useState(false);
  // Default to pre-selected (encourages "Accept all"), but not applied until user acts
  const [analytics, setAnalytics] = useState(true);
  const [ads, setAds] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = JSON.parse(localStorage.getItem(CONSENT_KEY));
      if (saved && typeof saved === 'object') {
        applyConsent(saved.analytics, saved.ads);
        setOpen(false);
      } else {
        setOpen(true);
      }
    } catch {
      setOpen(true);
    }
  }, []);

  const applyConsent = (analyticsGranted, adsGranted) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      'analytics_storage': analyticsGranted ? 'granted' : 'denied',
      'ad_storage': adsGranted ? 'granted' : 'denied',
      'ad_user_data': adsGranted ? 'granted' : 'denied',
      'ad_personalization': adsGranted ? 'granted' : 'denied',
    });
  };

  const acceptAll = () => {
    setAnalytics(true); setAds(true);
    applyConsent(true, true);
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ analytics: true, ads: true }));
    track('consent_update', { action: 'accept_all', analytics: true, ads: true });
    // Fire a SPA page_view after consent so GA4 receives a hit immediately
    try {
      track('spa_page_view', { page_path: window.location.pathname + window.location.search });
    } catch {}
    try { window.dispatchEvent(new CustomEvent('consent-updated', { detail: { analytics: true, ads: true } })); } catch {}
    setOpen(false);
  };
  const rejectAll = () => {
    setAnalytics(false); setAds(false);
    applyConsent(false, false);
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ analytics: false, ads: false }));
    track('consent_update', { action: 'reject_all', analytics: false, ads: false });
    try { window.dispatchEvent(new CustomEvent('consent-updated', { detail: { analytics: false, ads: false } })); } catch {}
    setOpen(false);
  };
  const save = () => {
    applyConsent(analytics, ads);
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ analytics, ads }));
    track('consent_update', { action: 'save', analytics, ads });
    // If analytics just got granted, also send a page_view hit
    if (analytics) {
      try {
        track('spa_page_view', { page_path: window.location.pathname + window.location.search });
      } catch {}
    }
    try { window.dispatchEvent(new CustomEvent('consent-updated', { detail: { analytics, ads } })); } catch {}
    setOpen(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
      <div className="relative h-full w-full flex items-center justify-center p-4 md:p-8">
        <div className="w-[90%] md:w-[80%] max-w-none max-h-[80vh] overflow-auto rounded-3xl shadow-2xl border border-gray-200 bg-white/95 backdrop-blur p-5 md:p-8 text-sm md:text-base">
          <div className="mb-3 md:mb-4 font-extrabold text-dark text-xl md:text-2xl">{t('consent.title')}</div>
          <p className="text-gray-700 mb-5 md:mb-6 leading-relaxed">{t('consent.description')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
            <label className="flex items-start gap-3 p-4 rounded-2xl bg-gray-50 border border-gray-200">
              <input type="checkbox" checked disabled className="mt-1" />
              <div>
                <div className="font-semibold text-gray-800">{t('consent.necessary')}</div>
                <div className="text-gray-600 text-xs md:text-sm">{t('consent.necessaryDesc')}</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-4 rounded-2xl hover:bg-gray-50 border border-gray-200">
              <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="mt-1" />
              <div>
                <div className="font-semibold text-gray-800">{t('consent.analytics')}</div>
                <div className="text-gray-600 text-xs md:text-sm">{t('consent.analyticsDesc')}</div>
              </div>
            </label>
            <label className="flex items-start gap-3 p-4 rounded-2xl hover:bg-gray-50 border border-gray-200">
              <input type="checkbox" checked={ads} onChange={(e) => setAds(e.target.checked)} className="mt-1" />
              <div>
                <div className="font-semibold text-gray-800">{t('consent.advertising')}</div>
                <div className="text-gray-600 text-xs md:text-sm">{t('consent.advertisingDesc')}</div>
              </div>
            </label>
          </div>
          <div className="flex flex-col md:flex-row gap-3 justify-end">
            <button onClick={rejectAll} className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 order-3 md:order-none">{t('consent.rejectAll')}</button>
            <button onClick={save} className="px-5 py-2.5 rounded-xl border border-primary text-primary hover:bg-green-50 order-2 md:order-none">{t('consent.save')}</button>
            <button onClick={acceptAll} className="px-5 py-2.5 rounded-xl bg-primary text-white hover:bg-green-700 shadow order-1 md:order-none">{t('consent.acceptAll')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
